import { Icon, toast, getTranslatedText } from '@nile/components';
import { DropZone } from '@shopify/polaris';
import {
  Button,
  Caption,
  Stack,
  TextContainer,
  Thumbnail,
  Modal,
  TextStyle
} from '@superops/ui-design-system';
import envConfig from '@nile/home/src/Config/env';
import { SVGIcon } from '@superops/super-icons';
import { FileImport } from '@styled-icons/fa-solid';
import { Delete } from '@styled-icons/material/Delete';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { FormattedMessage, injectIntl } from 'react-intl';
import { fileSizes } from '@nile/banyan/src/Constants/admin';
import { sumBy } from 'lodash';

const DeleteIcon = styled(Delete)`
  height: 20px;
  color: black;
  width: 20px;
`;

const ButtonContainer = styled.div`
  #addAttachmentsBtn {
    color: inherit;
  }
`;

const FILE_FORMATS = {
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  xls: 'application/vnd.ms-excel',
  csv: 'text/csv'
};

function blobToFile(theBlob, fileName) {
  // A Blob() is almost a File() - it's just missing the two properties below which we will add
  const blob = theBlob;
  blob.lastModifiedDate = new Date();
  blob.name = fileName;
  return blob;
}

function mimeType(extension) {
  switch (extension) {
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    case 'xls':
      return 'application/vnd.ms-excel';
    default:
      return 'text/csv';
  }
}

function FileUpload({ limitFileSize }) {
  return (
    <div className="Polaris-DropZone-FileUpload">
      <div className="Polaris-Stack Polaris-Stack--vertical">
        <div className="Polaris-Stack__Item">
          <img
            src={`${envConfig.imageRoot}svg/fileUpload.svg`}
            style={{ width: '134px' }}
            className="Polaris-DropZone-FileUpload__Image Polaris-DropZone-FileUpload--sizeExtraLarge"
          />
          {limitFileSize && (
            <div>
              <TextStyle variation="subdued">
                <FormattedMessage
                  id="common.upload.file.limit"
                  values={{
                    size: `${limitFileSize}MB`,
                    b: (chunks) => (
                      <TextStyle weight="SemiBold">{chunks}</TextStyle>
                    )
                  }}
                />
              </TextStyle>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function DropZoneSingle({
  file,
  setFile,
  limitFileSize,
  intl,
  acceptAllType,
  acceptType,
  allowIndividualClear = false
}) {
  const checkFileSize = (fileToCheck) => {
    const valueInBytes = limitFileSize * 1000000;
    return fileToCheck?.size < valueInBytes;
  };

  const customValidator = async (filesToValidate) => {
    let validatedFiles = [...filesToValidate];

    validatedFiles = filesToValidate.filter((fileToCheck, idx) => {
      if (!checkFileSize(fileToCheck)) {
        toast(
          'warning',
          getTranslatedText(intl, 'admin.upload.size.max.error', {
            size: `${limitFileSize}MB`
          })
        );
      }

      return checkFileSize(fileToCheck);
    });

    return validatedFiles;
  };

  const handleDropZoneDrop = useCallback(
    async (_dropFiles, acceptedFiles, _rejectedFiles) => {
      const fileToUpload = acceptedFiles[0];
      const selectedFile = _dropFiles?.[0] || {};
      let validatedFiles = [...acceptedFiles];
      if (limitFileSize) {
        validatedFiles = await customValidator(acceptedFiles);
      }
      const splitName = selectedFile.name.split('.');
      const type = splitName[splitName.length - 1];
      // type == '' and extention pkg then blob logic won't work
      if (type?.toLowerCase() === 'pkg' || type?.toLowerCase() === 'msi')
        return setFile(acceptedFiles[0]);

      /** Correct the file type format in CSV file */
      if (
        selectedFile.type === '' ||
        [FILE_FORMATS.xls, FILE_FORMATS.xlsx].indexOf(selectedFile.type) !== -1
      ) {
        // create a blob from file calling mime type injection function
        const blob = new Blob([selectedFile], { type: mimeType(type) });

        // Here you can use the file as you wish
        const newFile = blobToFile(blob, selectedFile.name);

        setFile(newFile);
        return undefined;
      }
      setFile(() => acceptedFiles[0]);
      return undefined;
    },
    []
  );

  const handleClearFile = useCallback(
    (event) => {
      event.stopPropagation();
      event.preventDefault();
      setFile(null);
    },
    [setFile]
  );

  const validImageTypes = ['text/csv'];

  // const fileUpload = !file && <DropZone.FileUpload />;
  const fileUpload = !file && <FileUpload limitFileSize={limitFileSize} />;
  const uploadedFile = file && (
    <Stack>
      <Thumbnail
        size="small"
        alt={file.name}
        source={
          validImageTypes.indexOf(file.type) > 0
            ? window.URL.createObjectURL(file)
            : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
        }
      />
      <div>
        {file.name} <Caption>{file.size} bytes</Caption>
      </div>
      {allowIndividualClear && (
        <Button onClick={handleClearFile} size="slim" plainSecondary>
          <SVGIcon name="Delete" />
        </Button>
      )}
    </Stack>
  );

  let filesToaccept = '.csv, text/csv';
  if (acceptAllType) {
    filesToaccept =
      'image/jpeg,image/png,image/svg+xml,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.spreadsheet,application/vnd.oasis.opendocument.presentation,application/vnd.oasis.opendocument.text,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,.ovpn';
  } else if (acceptType) {
    filesToaccept = acceptType;
  }
  return (
    <DropZone
      allowMultiple={false}
      onDrop={handleDropZoneDrop}
      accept={filesToaccept}
    >
      {uploadedFile}
      {fileUpload}
    </DropZone>
  );
}

export function DropZoneMultiple({
  files,
  setFiles,
  limitFileSize,
  intl,
  bulkFileValidator,
  acceptType,
  maxFiles,
  description = <></>,
  allowIndividualClear = false
}) {
  const checkFileSize = (fileToCheck) => {
    const valueInBytes = limitFileSize * 1000000;
    return fileToCheck?.size < valueInBytes;
  };

  const customValidator = async (filesToValidate) => {
    let validatedFiles = [...filesToValidate];

    if (bulkFileValidator) {
      if (!checkFileSize({ size: sumBy(validatedFiles, 'size') })) {
        toast(
          'warning',
          getTranslatedText(intl, 'admin.upload.size.max.error', {
            size: `${limitFileSize}MB`
          })
        );
        return [];
      }
      return validatedFiles;
    }

    validatedFiles = filesToValidate.filter((fileToCheck, idx) => {
      if (!checkFileSize(fileToCheck)) {
        toast(
          'warning',
          getTranslatedText(intl, 'admin.upload.size.max.error', {
            size: `${limitFileSize}MB`
          })
        );
      }

      return checkFileSize(fileToCheck);
    });

    return validatedFiles;
  };

  const handleDropZoneDrop = useCallback(
    async (_dropFiles, acceptedFiles, _rejectedFiles) => {
      let validatedFiles = [...acceptedFiles];

      if (limitFileSize) {
        validatedFiles = await customValidator(acceptedFiles);
      }

      if (maxFiles) {
        setFiles((currentFiles) =>
          [...currentFiles, ...validatedFiles].slice(0, maxFiles)
        );
        return;
      }

      setFiles((currentFiles) => [...currentFiles, ...validatedFiles]);
    },
    []
  );

  const handleRemoveFile = useCallback(
    (indexToRemove, event) => {
      event.stopPropagation();
      event.preventDefault();
      setFiles((currentFiles) =>
        currentFiles.filter((_, index) => index !== indexToRemove)
      );
    },
    [setFiles]
  );

  const validImageTypes = ['image/gif', 'image/jpeg', 'image/png'];

  // const fileUpload = !files.length && <DropZone.FileUpload />;
  const fileUpload = !files.length && (
    <FileUpload limitFileSize={limitFileSize} />
  );
  const uploadedFiles = files.length > 0 && (
    <Stack>
      {files.map((file, index) => (
        <Stack alignment="center" key={`${file.name}-${file.size}-${index}`}>
          <Thumbnail
            size="small"
            alt={file.name}
            source={
              validImageTypes.indexOf(file.type) > 0
                ? window.URL.createObjectURL(file)
                : 'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
            }
          />
          <div>
            {file.name} <Caption>{file.size} bytes</Caption>
          </div>
          {allowIndividualClear && (
            <Button
              onClick={(event) => handleRemoveFile(index, event)}
              size="slim"
              plainSecondary
            >
              <SVGIcon name="Delete" />
            </Button>
          )}
        </Stack>
      ))}
    </Stack>
  );

  return (
    <DropZone allowMultiple onDrop={handleDropZoneDrop} accept={acceptType}>
      {uploadedFiles}
      {fileUpload}
      {!files.length && description}
    </DropZone>
  );
}

function PromptModal(props) {
  const {
    limitFileSize = null,
    intl,
    bulkFileValidator = false,
    maxFiles,
    allowIndividualClear = false
  } = props;
  const [file, setFile] = useState(null);
  // For multiple file upload
  const [files, setFiles] = useState([]);

  const [active, setActive] = useState(false);
  const { modalActivator, icon, link, noIcon, svg } = props;
  const handleChange = useCallback(() => {
    setActive(!active);
    setFile(null);
    setFiles([]);
  }, [active]);

  const activator = (
    <Button onClick={handleChange}>
      <DeleteIcon />
    </Button>
  );

  const submitHandler = async (inpFile) => {
    await props.handleSubmit(inpFile);
  };

  const handlePrimaryAction = useCallback(() => {
    if (file) {
      if (props.handleSubmit) {
        submitHandler(file);
      }
      handleChange();
    }
    if (files.length) {
      if (props.handleSubmit) {
        submitHandler(files);
      }
      handleChange();
    }
  });

  return (
    <div>
      {modalActivator ? (
        <div
          onClick={handleChange}
          onKeyDown={(e) => e.key === 'Enter' && handleChange()}
          role="button"
          tabIndex={0}
          id={props.id}
        >
          {modalActivator}
        </div>
      ) : (
        <>
          {link ? (
            <>
              <span
                className="fx actr"
                id={props.id}
                onClick={handleChange}
                onKeyDown={(e) => e.key === 'Enter' && handleChange()}
                role="button"
                tabIndex={0}
              >
                {!noIcon && (
                  <Icon
                    style={{
                      width: 14,
                      height: 14,
                      marginRight: '10px',
                      color: 'inherit'
                    }}
                    icon={icon || FileImport}
                  />
                )}
                {svg}
                {props.btnText || ''}
              </span>
            </>
          ) : (
            <ButtonContainer>
              <Button
                id={props.id}
                onClick={handleChange}
                plain={link}
                monochrome
                icon={svg}
              >
                <span className="fx actr">
                  {!noIcon && (
                    <Icon
                      style={{
                        width: 14,
                        height: 14,
                        marginRight: '10px',
                        color: 'inherit'
                      }}
                      icon={icon || FileImport}
                    />
                  )}

                  {props.btnText || ''}
                </span>
              </Button>
            </ButtonContainer>
          )}
        </>
      )}
      <Modal
        // activator={activator} // check why this is not rendering
        open={active}
        onClose={handleChange}
        title={props.title || 'Prompt Modal'}
        primaryAction={{
          content: props.btnText || 'OK',
          onAction: handlePrimaryAction
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleChange
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>{props.description || 'Are you sure?'}</p>
            {props.allowMultiple ? (
              <DropZoneMultiple
                files={files}
                setFiles={setFiles}
                allowMultiple={props.allowMultiple}
                limitFileSize={limitFileSize}
                bulkFileValidator={bulkFileValidator}
                intl={intl}
                acceptType={props?.acceptType}
                maxFiles={maxFiles}
                allowIndividualClear={allowIndividualClear}
              />
            ) : (
              <DropZoneSingle
                file={file}
                setFile={setFile}
                limitFileSize={limitFileSize}
                intl={intl}
                acceptAllType={props?.acceptAllType}
                acceptType={props?.acceptType}
                allowIndividualClear={allowIndividualClear}
              />
            )}
          </TextContainer>
        </Modal.Section>
      </Modal>
    </div>
  );
}

export default injectIntl(PromptModal);
